import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SkeltonCardOne } from '../../../common/SkeltonCardOne';
import { getTicketPayments, cleanupTicketPaymentDetails } from '../../../actions/tickets';
import GridTable from '../../../common/GridTable';
import { GetPaymentsListLogic, TransactionStatus } from '../PaymentController';
import moment from 'moment';
import OverflowTip from '../../../common/OverflowTip';
import { validateDateValue, validateNumberValue } from '../../../utils/common';
import { paymentType, TransactionPurpose } from '../../Wallet/walletController';
import StatusLabel from '../reusableComponents/StatusLabel';
import IconToolTip from '../../../common/IconToolTip';
import { copyToClipboard } from '../../../actions/utils';
import { getServiceRequestPayments } from '../../../actions/service_request';
import { getAmount } from '../../Wallet/components/Passbook/Card';
import { TicketAgentRole } from '../../Ticket/TicketController';


const PaymentsDetails = ({ ticketId, user_type = 'ALL', ticket }) => {
    const serviceRequestPayments = useSelector((state) => state.serviceRequest.serviceRequestPayments);
    const dispatch = useDispatch();
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const isServiceRequestPaymentsLoading = useSelector((state) => state.loading.isServiceRequestPaymentsLoading);

    const memoizedCurrentTicketPayments = useMemo(() => {
        if (serviceRequestPayments && serviceRequestPayments[ticket.id]) {
            let list = serviceRequestPayments[ticket.id];

            const isLoginUserIsSp = ticket.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.SP);
            const isLoginUserIsScm = ticket.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.SCM);
            const isLoginUserIsCLuster = ticket.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.CLUSTER);
            const isLoginUserIsPsp = ticket.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.PSP);
            const isLoginUserIsSupervisor = ticket.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.SUPERVISOR);
            const isCustomer = ticket.helpdesk_id === newLoggedUserData.org.id;

            const isAdmin = isLoginUserIsScm || isLoginUserIsCLuster || isLoginUserIsPsp || isLoginUserIsSupervisor || isCustomer;

            list = list.filter((payment) => {
                if (isLoginUserIsSp && !isAdmin) {
                    return isLoginUserIsSp.milestone_id === payment.milestone_id;
                }
                return true;
            });

            return list;
        }
        return [];
    }, [serviceRequestPayments, ticket.id, ticket.agents, newLoggedUserData.user.id, newLoggedUserData.org.id, ticket.helpdesk_id]);

    useEffect(() => {
        if (ticket?.id) {
            let roles = ticket.agents.filter(agent => agent.agent.id === newLoggedUserData.user.id).map(agent => agent.role);
            dispatch(getServiceRequestPayments({ ticketId: ticket.id, roles, org_id: ticket.helpdesk_id }));
        }
    }, [ticketId, newLoggedUserData.user.id, dispatch, ticket.helpdesk_id, ticket.agents, ticket.id]);

    const [transformedPaymentData, setTransformedPaymentData] = useState([]);

    const isToShowBasePaymentColumnInTicketPaymentDetails = GetPaymentsListLogic('isToShowBasePaymentColumnInTicketPaymentDetails');

    const StatusBox = ({ status }) => {

        const classLookup = {
            APPROVED: 'scogo2e',
            FAILURE: 'scogoclosed',
            SCHEDULED: 'scogoorange',
            PENDING: 'scogoorange',
            REJECTED: 'scogoclosed',
            SUCCESS: 'scogo2e',
        };
        return <StatusLabel content={status} color={classLookup[status]} />;
    };

    const getTransformedStatus = (status, paymentType, orderStatus) => {
        let label = 'Pending';
        if (['WITHDRAW', 'BANK_TRANSFER_CREDIT'].includes(paymentType)) {
            if (status === '200' && ['15', 15].includes(orderStatus)) {
                label = 'Success';
            } else if (['17', 17].includes(orderStatus)) {
                label = 'Rejected';
            } else {
                label = 'Pending';
            }
        } else if (status === 'Success') {
            label = 'Approved';
        } else if (status === 'Failure') {
            label = 'Rejected';
        } else if (status === 'Scheduled') {
            label = 'Scheduled';
        }
        return label;
    };

    const isToShowPaymentStatus = (payment) => {
        return [TransactionStatus.SUCCESS, TransactionStatus.FAILURE, TransactionStatus.SCHEDULED, TransactionStatus.PROCESSING].includes(payment.status) && (payment.processed_by_details || payment.created_by_details);
    }

    const copyTransaction = data => {
        let content = '';
        let sender = 'Scogo';
        // if (loggedUser.manage_partner_payments === 1 && loggedUser.customer_company_name) {
        //     sender = loggedUser.customer_company_name;
        // }

        let payee = 'You';
        if (data.user) payee = data.user;

        content = '';
        content += `${sender} has paid ${payee}\n`;
        content += `Amount: ${data.orderAmount}\n`;
        content += `Mode of Payment: ${data.paymentMode}\n`;
        content += `${sender} Transaction No: ${data.transactionNo}\n`;
        if (ticket && ticket.job_ticket_number) content += `Ticket Number: ${ticket.job_ticket_number}\n`;
        content += `Transaction Time: ${moment(data.txTime).add(5.50, 'hours').format('DD-MM-YYYY HH:mm:ss')}\n`;
        if (data.txMsg) {
            content += `Remark: ${data.txMsg}`;
        }
        dispatch(copyToClipboard({ data: content }))
    }

    const copyTransactionInvoice = data => {
        let content = '';
        let sender = 'Scogo';
        // if (loggedUser.manage_partner_payments === 1 && loggedUser.customer_company_name) {
        //     sender = loggedUser.customer_company_name;
        // }

        let payee = 'You';
        if (data.user) payee = data.user;

        content = '';
        content += `${sender} has paid ${payee}\n`;
        content += `Amount: ${data.orderAmount}\n`;
        content += `Mode of Payment:  VENDOR\n`;
        content += `${sender} Invoice No: ${data.referenceId}\n`;
        if (ticket && ticket.job_ticket_number) content += `Ticket Number: ${ticket.job_ticket_number}\n`;
        content += `Transaction Time: ${moment(data.txTime).add(5.50, 'hours').format('DD-MM-YYYY HH:mm:ss')}\n`;
        if (data.txMsg) {
            content += `Remark: ${data.txMsg}`;
        }
        dispatch(copyToClipboard({ data: content }))
    }

    const bluePrint = {
        table: [
            {
                head: {
                    render: (headName) => {
                        return headName;
                    },
                    headName: 'Transaction No',
                    width: 1.5
                },
                body: {
                    render: (payment) => {
                        let id = payment.id;
                        if (payment.amount_type === 'OFFLINE') {
                            id = payment.reference_id;
                        }
                        let name = payment.created_by_details.display_name;
                        if (payment?.sender?.display_name) name = payment?.sender?.display_name;
                        const status = payment.status;
                        return (
                            <div>
                                <div className='flex gap-2 items-center'>
                                    {[TransactionPurpose.TRANSFER, TransactionPurpose.WITHDRAW].includes(payment.purpose) ? <span className={`material-icons text-[1.5rem] ${status === TransactionStatus.INITIATED ? 'text-blue-400' :
                                        status === TransactionStatus.PENDING ? 'text-yellow-400' :
                                            status === TransactionStatus.SCHEDULED ? 'text-orange-400' :
                                                status === TransactionStatus.PROCESSING ? 'text-blue-400' :
                                                    status === TransactionStatus.SUCCESS ? 'text-green-400' :
                                                        status === TransactionStatus.FAILURE ? 'text-red-400' :
                                                            status === TransactionStatus.REJECTED ? 'text-red-400' :
                                                                'text-gray-400'
                                        }`}>
                                        account_balance
                                    </span> : null}
                                    <OverflowTip textClass={'text-scogoprimary font-medium cursor-pointer'} someLongText={id} />
                                </div>
                                {name ? (
                                    <span className='w-full flex items-center gap-1'>
                                        <p className='text-medium'>Requested by:</p>
                                        <OverflowTip textClass='font-medium' someLongText={`${name}`} />
                                    </span>
                                ) : (
                                    null
                                )}
                                {payment?.created_at ? <span className='w-full flex items-center gap-2 text-sm'>
                                    <p className='text-normal'>Created At:</p>
                                    <OverflowTip textClass='font-bold' someLongText={`${moment(payment?.created_at).format('DD-MM-YYYY HH:mm:ss')}`} />
                                </span> : null}
                                {payment?.scheduled_at ? <span className='w-full flex items-center gap-2 text-sm'>
                                    <p className='text-normal'>Scheduled at:</p>
                                    <OverflowTip textClass='font-bold' someLongText={`${moment(payment?.scheduled_at).format('DD-MM-YYYY HH:mm:ss')}`} />
                                </span> : null}
                            </div>
                        );
                    },
                },
            },
            {
                head: {
                    render: (headName) => {
                        return headName;
                    },
                    headName: 'User',
                },
                body: {
                    render: (payment) => {
                        return (
                            <div>
                                <OverflowTip someLongText={payment?.receiver?.display_name || payment?.user?.display_name || payment?.beneficiary_organization?.name || ''}></OverflowTip>
                                {payment.ticket_agent_role ? <span className='max-w-max text-sm px-2 py-1 rounded-lg text-gray-800 font-medium bg-blue-300'>{payment.ticket_agent_role}</span> : null}
                            </div>
                        );
                    },
                },
            },
            {
                head: {
                    render: (headName) => {
                        return headName;
                    },
                    headName: 'Details',
                    width: 1.2
                },
                body: {
                    render: (payment) => {
                        return <PaymentColumn payment={payment} />;
                    },
                },
            },
            {
                head: {
                    render: (headName) => {
                        return headName;
                    },
                    headName: 'Amount',
                },
                body: {
                    render: (payment, rowIndex) => {
                        let amount = getAmount(payment);
                        let text_color = amount >= 0 && [TransactionStatus.SUCCESS, TransactionStatus.INITIATED, TransactionStatus.PENDING, TransactionStatus.PROCESSING, TransactionStatus.SCHEDULED].includes(payment.status) ? 'text-green-500' : 'text-red-500';
                        return (
                            <div className='flex items-center justify-between w-full'>
                                {amount >= 0 ? <div className={text_color}>₹ {amount}</div> : <></>}
                                {amount < 0 ? <div className={text_color}>₹ {amount}</div> : <></>}
                            </div>
                        );
                    },
                },
            },
            {
                head: {
                    render: (headName) => {
                        return headName;
                    },
                    headName: 'Status',
                },
                body: {
                    render: (payment, rowIndex) => {
                        const showCopyIcon = ['WITHDRAW', 'BANK_TRANSFER_CREDIT',].includes(payment.paymentType);
                        return (
                            <div>
                                <div className='flex'>
                                    <StatusBox status={payment?.status} />
                                    {showCopyIcon && <IconToolTip placement={'bottom'} title={`Copy Serial Number`}>
                                        <span className='hover:text-scogoorange cursor-pointer material-icons text-scogobgsky text-lg pl-1' onClick={() => copyTransaction(payment)}>
                                            content_copy
                                        </span>
                                    </IconToolTip>}
                                    {payment.reason === 'INVOICE' && <IconToolTip placement={'bottom'} title={`Copy Serial Number`}>
                                        <span className='hover:text-scogoorange cursor-pointer material-icons text-scogobgsky text-lg pl-1' onClick={() => copyTransactionInvoice(payment)}>
                                            content_copy
                                        </span>
                                    </IconToolTip>}
                                </div>
                                {isToShowPaymentStatus(payment) && (
                                    <div className='pt-2'>

                                        {payment?.processed_by_details ? <small>
                                            by <b>{`${payment?.processed_by_details?.display_name}`}</b>
                                        </small> : <small>
                                            by <b>{`${payment?.created_by_details?.display_name}`}</b>
                                        </small>}
                                        {payment?.processed_at ? (
                                            <div className='truncate w-4/5'>
                                                <OverflowTip
                                                    someLongText={

                                                        <small>
                                                            On <b>{`${moment(payment?.processed_at).format('DD-MM-YYYY HH:mm:ss')}`}</b>
                                                        </small>
                                                    }
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        );
                    },
                },
            },
        ],
    };

    if (isServiceRequestPaymentsLoading) return <SkeltonCardOne />;

    return (
        <div className='px-7 pb-8 py-4 h-[50vh]'>
            <p className='text-medium text-md'>
                <b>*</b>All payments from Scogo are made post deduction of TDS and System Charges
            </p>
            <GridTable schema={bluePrint} rowData={memoizedCurrentTicketPayments} tableBodyHeight={'auto'} tableError='No Ticket Found' />
        </div>
    );
};

export default PaymentsDetails;

const PaymentColumn = ({ payment }) => {
    const { basePaymentDetails, paymentType } = payment;
    const isBasePayment = basePaymentDetails === 'BASE PAYMENT';
    const isIncentivePaymentType = paymentType === 'INCENTIVE';
    const isWithdrawlType = ['WITHDRAW', 'BANK_TRANSFER_CREDIT'].includes(paymentType);

    const commonColorCss = 'text-scogogray';

    const getIncentiveDetails = () => {
        return (
            <>
                {isIncentivePaymentType && <p className={commonColorCss}>INCENTIVE</p>}
                {!isIncentivePaymentType && (
                    <>
                        {payment.category && <span className='w-full flex items-center gap-2'>
                            <p className={commonColorCss}>Category:</p>
                            <OverflowTip someLongText={payment.category} />
                        </span>}
                        {isWithdrawlType && <p className={commonColorCss}>WITHDRAW</p>}
                        <span className='w-full flex items-center gap-2'>
                            <p className={commonColorCss}>Remark:</p>
                            <OverflowTip someLongText={payment.remarks} />
                        </span>
                        {/* {payment.statement && <span className='w-full flex items-center gap-2'>
                            <p className={commonColorCss}>Remark:</p>
                            <OverflowTip someLongText={payment.statement} />
                        </span>} */}
                        {payment.reason === 'INVOICE' && <span className='w-full flex items-center gap-2'>
                            <p className={commonColorCss}>Invoice:</p>
                            <OverflowTip someLongText={payment?.referenceId} />

                        </span>}
                    </>
                )}
            </>
        );
    };
    return (
        <div className='truncate w-full'>
            {basePaymentDetails && <p className={commonColorCss}>{basePaymentDetails}</p>}
            {!isBasePayment && getIncentiveDetails()}
        </div>
    );
};
